// composables/useUserStore.ts
import { ref } from 'vue';
import {
    fetchUserGalleries,
    fetchUserWorks,
    fetchWorks,
    postWork,
    updateGallery,
    updateWork,
    removeWork,
    fetchOtherUserWorks,
    fetchWork,
    fetchBrandFeed,
    postGallery,
    removeGallery,
    fetchGallery,
    fetchHome,
    fetchPreview,
    fetchSearchFeed,
    updateWorkOrders
} from "~/api/workApi";
import type {Gallery, Work} from "~/types/types";
import {useUserStore} from "~/composables/useUserStore";
import {visit} from "yaml";
import {updateReadOrders} from "~/api/tagApi";

const works = ref<Work[]>([]);
const galleries = ref<Gallery[]>([]);
const showEditBoardPopup = ref(false);
const showDeleteBoardPopup = ref(false);
const currentBoard = ref<Gallery | any>(null);

export function useWorkStore() {
    const {token, visitingToken} = useUserStore();
    // Functions to toggle popups
    const toggleEditBoardPopup = (board: Gallery | null = null) => {
        currentBoard.value = board;
        showEditBoardPopup.value = !showEditBoardPopup.value;
        console.log('showEditBoardPopup', showEditBoardPopup.value);
    };

    const toggleDeleteBoardPopup = (board: Gallery | null = null) => {
        currentBoard.value = board;
        showDeleteBoardPopup.value = !showDeleteBoardPopup.value;
    };

    const loadWork = async (id: string) => {
        try {
            const work = await fetchWork(token.value, id);
            return work;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadGallery = async (id: string) => {
        try {
            const gallery = await fetchGallery(token.value, id);
            return gallery;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadWorks = async (query = {}) => {
        try {
            works.value = await fetchWorks(query);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadHome = async (query = {}) => {
        try {
            return await fetchHome(query);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadBrandFeed = async (query = {}) => {
        try {
            return await fetchBrandFeed(query, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const updateWorkOrder = async (updatedWorks: any[]) => {
        try {
            const response = await updateWorkOrders({works: updatedWorks}, token.value);
            return response;
        } catch (error) {
            console.error('Failed to update tag order:', error);
        }
    };
    const searchFeed = async (query = {} as any) => {
        try {
            if (query.search) {
                return await fetchSearchFeed(query, token.value);
            } else {
                return await fetchBrandFeed(query, token.value);
            }
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadPreviewFeed = async (query = {}) => {
        try {
            return await fetchPreview(query);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadUserWorks = async (query = {} as any) => {
        try {
            return await fetchUserWorks(query, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadProfileWorks = async (query = {} as any) => {
        try {
            return await fetchOtherUserWorks(query, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadVisitingUserWorks = async () => {
        try {
            return await fetchUserWorks({}, visitingToken.value);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadUserGalleries = async () => {
        try {
            const response = await fetchUserGalleries(token.value);
            galleries.value = response;
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadVisitingUserGalleries = async () => {
        try {
            const response = await fetchUserGalleries(visitingToken.value);
            galleries.value = response;
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const newWork = async (payload: any) => {
        try {
            const response = await postWork(payload, token.value);
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const patchWork = async (payload: any, id: string) => {
        try {
            const response = await updateWork(payload, id, token.value);
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const deleteWork = async (id: string) => {
        try {
            const response = await removeWork(id, token.value);
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const deleteGallery = async (id: string) => {
        try {
            const response = await removeGallery(id, token.value);
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const createGallery = async (payload: any) => {
        try {
            const response = await postGallery(payload, token.value);
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const patchGallery = async (payload: any, id: string) => {
        try {
            const response = await updateGallery(payload, id, token.value);
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    return {
        works,
        galleries,
        loadWork,
        loadWorks,
        loadHome,
        loadVisitingUserWorks,
        loadUserWorks,
        loadUserGalleries,
        loadVisitingUserGalleries,
        newWork,
        patchWork,
        deleteWork,
        deleteGallery,
        patchGallery,
        createGallery,
        loadBrandFeed,
        loadPreviewFeed,
        loadGallery,
        loadProfileWorks,
        searchFeed,
        showEditBoardPopup,
        showDeleteBoardPopup,
        currentBoard,
        toggleDeleteBoardPopup,
        toggleEditBoardPopup,
        updateWorkOrder
    };
}
